import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  DURATE_COLABORARE,
  ETICHETE_DURATA_COLABORARE,
  ETICHETE_TIP_COLABORARE,
  TIPURI_COLABORARE,
  type DurataColaborare,
  type TipColaborare,
} from '@b4l/shared'
import { api } from '../../../lib/api'
import { Modal } from '../../../components/ui/Modal'
import { Button } from '../../../components/ui/Button'
import { Field } from '../../../components/ui/Field'
import { eroare, type Colaborare } from './comun'

const camp = 'w-full rounded border border-b4l-border bg-b4l-surface px-3 py-2 text-sm'
const eticheta = 'mb-1 block text-xs font-medium text-b4l-text-muted'

/**
 * Colaborare noua sau modificata. La medic se cer doar datele profesionale (specialitate,
 * unitate, oras, un contact profesional) — fara CNP, adresa de acasa sau alte date personale.
 * Firmele (clinica, farmacia, brandul) se pot lega de partenerul comun, daca exista deja.
 */
export function FormularColaborare({
  initial,
  responsabili,
  onInchide,
}: {
  initial?: Colaborare
  /** Doar pentru admin: pe cine se trece colaborarea. Gol = pe cine o scrie. */
  responsabili: { id: number; name: string }[]
  onInchide: (salvata?: Colaborare) => void
}) {
  const qc = useQueryClient()
  const [tip, setTip] = useState<TipColaborare>(initial?.tip ?? 'medic')
  const [durata, setDurata] = useState<DurataColaborare>(initial?.durata ?? 'continua')
  const [denumire, setDenumire] = useState(initial?.denumire ?? '')
  const [partener, setPartener] = useState(initial?.partener ?? null)
  const [cautare, setCautare] = useState('')
  const [specialitate, setSpecialitate] = useState(initial?.specialitate ?? '')
  const [unitate, setUnitate] = useState(initial?.unitate ?? '')
  const [localitate, setLocalitate] = useState(initial?.localitate ?? '')
  const [contact, setContact] = useState(initial?.contact ?? '')
  const [dataSemnare, setDataSemnare] = useState(initial?.dataSemnare ?? '')
  const [valabilDe, setValabilDe] = useState(initial?.valabilDe ?? '')
  const [valabilPana, setValabilPana] = useState(initial?.valabilPana ?? '')
  const [obiect, setObiect] = useState(initial?.obiect ?? '')
  const [valoare, setValoare] = useState(initial?.valoare ?? '')
  const [conditii, setConditii] = useState(initial?.conditii ?? '')
  const [activa, setActiva] = useState(false)
  const [responsabilId, setResponsabilId] = useState(initial ? String(initial.responsabil.id) : '')

  const esteFirma = tip !== 'medic'
  const gasiti = useQuery({
    queryKey: ['colaborari', 'parteneri', cautare],
    queryFn: async () =>
      (
        await api.get<{
          parteneri: {
            id: number
            denumire: string
            codFiscal: string | null
            localitate: string | null
          }[]
        }>('/colaborari/parteneri', { params: { q: cautare } })
      ).data.parteneri,
    enabled: esteFirma && cautare.trim().length >= 2,
  })

  const salveaza = useMutation({
    mutationFn: async () => {
      const corp = {
        tip,
        durata,
        partenerId: esteFirma ? (partener?.id ?? null) : null,
        denumire: denumire.trim(),
        specialitate: tip === 'medic' ? specialitate : '',
        unitate,
        localitate,
        contact,
        dataSemnare,
        valabilDe,
        valabilPana,
        obiect,
        valoare: valoare === '' ? null : Number(valoare),
        conditii,
      }
      if (initial) {
        const r = await api.patch<{ colaborare: Colaborare }>(`/colaborari/${initial.id}`, {
          ...corp,
          ...(responsabilId && Number(responsabilId) !== initial.responsabil.id
            ? { responsabilId: Number(responsabilId) }
            : {}),
        })
        return r.data.colaborare
      }
      const r = await api.post<{ colaborare: Colaborare }>('/colaborari', {
        ...corp,
        stare: activa ? 'activ' : 'negociere',
        ...(responsabilId ? { responsabilId: Number(responsabilId) } : {}),
      })
      return r.data.colaborare
    },
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      toast.success(initial ? 'Colaborarea a fost salvată.' : 'Colaborarea a fost adăugată.')
      onInchide(c)
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const trimite = () => {
    const lipsa =
      !denumire.trim() && !partener
        ? 'Scrie numele partenerului.'
        : !valabilDe
          ? 'Alege începutul perioadei.'
          : obiect.trim().length < 3
            ? 'Scrie pe scurt obiectul colaborării.'
            : activa && !dataSemnare
              ? 'O colaborare activă are data semnării.'
              : null
    if (lipsa) toast.error(lipsa)
    else salveaza.mutate()
  }

  return (
    <Modal
      open
      onClose={() => onInchide()}
      size="lg"
      title={initial ? `Modifică: ${initial.denumire}` : 'Colaborare nouă'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onInchide()}>
            Renunță
          </Button>
          <Button onClick={trimite} loading={salveaza.isPending}>
            {initial ? 'Salvează' : 'Adaugă'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={eticheta}>Tip</label>
            <select
              value={tip}
              onChange={(e) => setTip(e.target.value as TipColaborare)}
              className={camp}
            >
              {TIPURI_COLABORARE.map((t) => (
                <option key={t} value={t}>
                  {ETICHETE_TIP_COLABORARE[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={eticheta}>Durată</label>
            <select
              value={durata}
              onChange={(e) => setDurata(e.target.value as DurataColaborare)}
              className={camp}
            >
              {DURATE_COLABORARE.map((d) => (
                <option key={d} value={d}>
                  {ETICHETE_DURATA_COLABORARE[d]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {esteFirma && (
          <div className="rounded border border-b4l-border p-3">
            <label className={eticheta}>Partener din CRM (opțional)</label>
            {partener ? (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>
                  {partener.denumire}
                  {partener.codFiscal && (
                    <span className="text-b4l-text-muted"> · {partener.codFiscal}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="text-xs text-b4l-blue-dark underline"
                  onClick={() => setPartener(null)}
                >
                  Scoate legătura
                </button>
              </div>
            ) : (
              <>
                <input
                  value={cautare}
                  onChange={(e) => setCautare(e.target.value)}
                  placeholder="Caută după nume sau cod fiscal…"
                  className={camp}
                />
                {(gasiti.data ?? []).length > 0 && (
                  <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-b4l-border text-sm">
                    {gasiti.data!.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-1.5 text-left hover:bg-b4l-bg"
                          onClick={() => {
                            setPartener({ id: p.id, denumire: p.denumire, codFiscal: p.codFiscal })
                            if (!denumire.trim()) setDenumire(p.denumire)
                            setCautare('')
                          }}
                        >
                          {p.denumire}
                          <span className="text-b4l-text-muted">
                            {p.codFiscal ? ` · ${p.codFiscal}` : ''}
                            {p.localitate ? ` · ${p.localitate}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1 text-xs text-b4l-text-muted">
                  Dacă firma nu e în CRM, scrie doar numele mai jos. De aici nu se creează parteneri
                  noi în contabilitate.
                </p>
              </>
            )}
          </div>
        )}

        <Field
          label={tip === 'medic' ? 'Numele medicului' : 'Denumirea'}
          value={denumire}
          onChange={(e) => setDenumire(e.target.value)}
          placeholder={tip === 'medic' ? 'Dr. Ana Popescu' : 'Clinica X'}
        />
        {tip === 'medic' && (
          <p className="-mt-2 text-xs text-b4l-text-muted">
            Doar date profesionale. Nu trece CNP, adresa de acasă sau alte date personale.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          {tip === 'medic' && (
            <Field
              label="Specialitatea"
              value={specialitate}
              onChange={(e) => setSpecialitate(e.target.value)}
            />
          )}
          <Field
            label={tip === 'medic' ? 'Unitatea unde profesează' : 'Unitatea / punctul'}
            value={unitate}
            onChange={(e) => setUnitate(e.target.value)}
          />
          <Field
            label="Localitatea"
            value={localitate}
            onChange={(e) => setLocalitate(e.target.value)}
          />
          <Field
            label="Contact profesional"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="email sau telefon de serviciu"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Data semnării"
            type="date"
            value={dataSemnare}
            onChange={(e) => setDataSemnare(e.target.value)}
          />
          <Field
            label="Valabil de la"
            type="date"
            value={valabilDe}
            onChange={(e) => setValabilDe(e.target.value)}
          />
          <Field
            label="Valabil până la"
            type="date"
            value={valabilPana}
            onChange={(e) => setValabilPana(e.target.value)}
            hint="Gol = nedeterminată"
          />
        </div>

        <div>
          <label className={eticheta}>Obiectul colaborării</label>
          <textarea
            value={obiect}
            onChange={(e) => setObiect(e.target.value)}
            rows={3}
            className={camp}
            placeholder="Ce face fiecare parte: recomandări, training, materiale în clinică…"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Valoare (lei, opțional)"
            type="number"
            min="0"
            step="0.01"
            value={valoare}
            onChange={(e) => setValoare(e.target.value)}
          />
          <div className="col-span-2">
            <label className={eticheta}>Condiții (opțional)</label>
            <textarea
              value={conditii}
              onChange={(e) => setConditii(e.target.value)}
              rows={2}
              className={camp}
              placeholder="Produse în schimb, comision, frecvență…"
            />
          </div>
        </div>

        {responsabili.length > 0 && (
          <div>
            <label className={eticheta}>Responsabil</label>
            <select
              value={responsabilId}
              onChange={(e) => setResponsabilId(e.target.value)}
              className={camp}
            >
              {!initial && <option value="">Eu</option>}
              {responsabili.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {!initial && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={activa}
              onChange={(e) => setActiva(e.target.checked)}
              className="h-4 w-4"
            />
            Contractul e deja semnat — colaborarea pornește activă
          </label>
        )}
      </div>
    </Modal>
  )
}
